import { AlertCircle, Plane } from '../../icons';
import type { AttendanceCalendarDay } from '../../api/hr-attendance-calendar';
import { leaveTypeLabel, tripTypeLabel } from '../../lib/leave-type';
import { leaveTimeRangeText } from '../../lib/partial-leave-time';
import './people-month-grid.css';

/**
 * 근태 현황 «사람별» — 사람 × 그 달 날짜 격자 (2026-10-18 · web 레인 W10-23a · 렌즈 Few · 계획 8.2)
 *
 * 이상이 «누구 · 어느 날»인지 한 화면에 보이게 한다. **지금 API 만** 쓴다.
 *   · 휴가 · 출장 · 공휴일 — `fetchAttendanceCalendar({ period: 'month' })` 의 `days[].items` · `holiday`
 *   · 줄 끝 «이 달 지각 · 퇴근 누락» — `getAttendanceRange({ period: 'month' })` 의 `lateDays` · `openDays`
 *     (`openDays` = 퇴근을 안 찍은 날 · 오늘은 안 센다)
 *   · 오늘 칸의 «아직 출근 기록 없음» — 그날 근태(`getTeamAttendance`)의 `state === 'ABSENT'`(재평 23 고칠 1)
 *   ★ 칸마다 지각 · 퇴근 누락은 **찍지 않는다** — 지금 API 로는 날짜별 값이 없다(짝 문서 T30 · W10-23b · 작업 가지).
 *   ★ 휴가는 옅은 채움 + «휴» 글자(색에만 기대지 않는다), 공휴일은 빗금 — 둘이 같은 회색으로 섞이지 않게(재평 23 고칠 2).
 *   ★ 토 · 일은 머리만 회색이다 — 근무제가 달라 «쉬는 날»로 칠할 근거가 없다(시안 23: «일 · 토 머리 회색»).
 */
export interface PeopleMonthGridPerson {
  userId: string;
  name: string;
  teamName: string | null;
}

export interface PeopleMonthGridProps {
  /** 그 달의 날만(`inMonth`). */
  days: readonly AttendanceCalendarDay[];
  people: readonly PeopleMonthGridPerson[];
  /** 줄 끝 합계 — 못 읽었으면(옛 서버 · 권한) null 이고 그 칸은 «—» 다. */
  totals: ReadonlyMap<string, { late: number; open: number }> | null;
  /** 오늘(`YYYY-MM-DD`) — 오늘 칸에만 «아직 출근 기록 없음»을 찍는다. */
  today: string;
  /** 오늘 아직 출근 기록이 없는 사람 — 그날 근태를 오늘로 읽었을 때만 준다. */
  absentToday: ReadonlySet<string>;
  /** 표 아래가 보는 날 — 머리를 누르면 그날로 바뀐다. */
  selected: string;
  onSelect: (date: string) => void;
  caption: string;
}

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'] as const;
const weekdayOf = (iso: string) => new Date(`${iso}T00:00:00Z`).getUTCDay();

export function PeopleMonthGrid({
  days, people, totals, today, absentToday, selected, onSelect, caption,
}: PeopleMonthGridProps) {
  // 날 × 사람 → 그 칸의 휴가 · 출장(서버가 준 그대로 — 한 사람 한 날에 여럿이면 모두)
  const byCell = new Map<string, AttendanceCalendarDay['items']>();
  for (const d of days) {
    for (const it of d.items) {
      const k = `${d.date}|${it.userId}`;
      byCell.set(k, [...(byCell.get(k) ?? []), it]);
    }
  }

  return (
    <section className="tmpg" aria-label="사람별 근태">
      <p className="tmpg__legend">
        <span><span className="tmpg__mark tmpg__mark--leave" aria-hidden>휴</span> 휴가</span>
        <span><Plane className="tmpg__trip" aria-hidden /> 출장 · 외근</span>
        <span><span className="tmpg__swatch tmpg__swatch--off" aria-hidden /> 공휴일</span>
        <span><AlertCircle className="tmpg__absent" aria-hidden /> 오늘 아직 출근 기록 없음</span>
        <span className="tmpg__legendnote">지각 · 퇴근 누락은 줄 끝에 이 달 합계로 적습니다 — 날짜별 표시는 서버가 준비되면 붙입니다</span>
      </p>
      <div className="tmpg__scroll">
        {/* 머리 단추로 날을 고르는 상호작용 격자 — role=grid(아래 날짜 표 «table» 과 갈린다 · 달력 CalendarGrid 와 같은 역할) */}
        <table className="tmpg__table" role="grid" aria-label={caption}>
          <caption className="tmpg__sr">{caption}</caption>
          <thead>
            <tr>
              <th scope="col" className="tmpg__who">이름</th>
              {days.map((d) => {
                const wd = weekdayOf(d.date);
                const day = Number(d.date.slice(8));
                const cls = [
                  'tmpg__day',
                  wd === 0 || wd === 6 ? 'is-weekend' : '',
                  d.holiday ? 'is-off' : '',
                  d.date === today ? 'is-today' : '',
                  d.date === selected ? 'is-on' : '',
                ].filter(Boolean).join(' ');
                return (
                  <th key={d.date} scope="col" className={cls} title={d.holiday?.name}>
                    <button
                      type="button"
                      aria-label={`${d.date} (${WEEKDAY[wd]})${d.holiday ? ` · ${d.holiday.name}` : ''}`}
                      aria-pressed={d.date === selected}
                      onClick={() => onSelect(d.date)}
                    >
                      <b>{day}</b><small>{WEEKDAY[wd]}</small>
                    </button>
                  </th>
                );
              })}
              <th scope="col" className="tmpg__sum">지각</th>
              <th scope="col" className="tmpg__sum">퇴근 누락</th>
            </tr>
          </thead>
          <tbody>
            {people.map((p) => {
              const t = totals?.get(p.userId);
              return (
                <tr key={p.userId}>
                  <th scope="row" className="tmpg__who">
                    <b>{p.name}</b>
                    {p.teamName && <small>{p.teamName}</small>}
                  </th>
                  {days.map((d) => {
                    const items = byCell.get(`${d.date}|${p.userId}`) ?? [];
                    const leave = items.find((it) => it.kind === 'LEAVE');
                    const trip = items.find((it) => it.kind === 'TRIP');
                    const absent = d.date === today && absentToday.has(p.userId);
                    const cls = ['tmpg__cell', d.holiday ? 'is-off' : '', leave ? 'is-leave' : ''].filter(Boolean).join(' ');
                    return (
                      <td key={d.date} className={cls} data-date={d.date}>
                        {leave && (
                          <span className="tmpg__mark tmpg__mark--leave" title={leaveText(leave)}>
                            <span aria-hidden>휴</span><span className="tmpg__sr">{leaveText(leave)}</span>
                          </span>
                        )}
                        {trip && (
                          <span title={tripTypeLabel(trip.type) ?? '출장'}>
                            <Plane className="tmpg__trip" aria-hidden /><span className="tmpg__sr">{tripTypeLabel(trip.type) ?? '출장'}</span>
                          </span>
                        )}
                        {absent && (
                          <span title="아직 출근 기록 없음">
                            <AlertCircle className="tmpg__absent" aria-hidden /><span className="tmpg__sr">아직 출근 기록 없음</span>
                          </span>
                        )}
                        {d.holiday && !leave && !trip && !absent && <span className="tmpg__sr">{d.holiday.name}</span>}
                      </td>
                    );
                  })}
                  <td className="tmpg__sum num">{t ? t.late : '—'}</td>
                  <td className="tmpg__sum num">{t ? t.open : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function leaveText(it: AttendanceCalendarDay['items'][number]): string {
  const range = leaveTimeRangeText(it);
  return `${leaveTypeLabel(it.type) ?? '휴가'}${range ? ` ${range}` : ''}`;
}
